import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
import ModuleGuard from './components/ModuleGuard';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import CompleteSetup from './pages/CompleteSetup';
import MainHubLogin from './pages/MainHubLogin';
import Dashboard from './pages/Dashboard';
import Cases from './pages/Cases';
import CaseCreatePage from './pages/CaseCreatePage';
import CaseDetail from './pages/CaseDetail';
import OpsPlanSharedPage from './pages/OpsPlanSharedPage';
import EvidenceUploadPage from './pages/EvidenceUploadPage';
import EvidencePreviewPage from './pages/EvidencePreviewPage';
import TeamMemberFormPage from './pages/TeamMemberFormPage';
import ApplicantReviewPage from './pages/ApplicantReviewPage';
import CaseLogs from './pages/CaseLogs';
import CaseNotes from './pages/CaseNotes';
import Evidence from './pages/Evidence';
import Reports from './pages/Reports';
import TeamManagement from './pages/TeamManagement';
import Analytics from './pages/Analytics';
import CreateReport from './pages/CreateReport';
import Clients from './pages/Clients';
import ClientFormPage from './pages/ClientFormPage';
import ClientViewPage from './pages/ClientViewPage';
import SubjectFormPage from './pages/SubjectFormPage';
import VehicleFormPage from './pages/VehicleFormPage';
import LogFormPage from './pages/case/LogFormPage';
import NoteFormPage from './pages/case/NoteFormPage';
import LeadFormPage from './pages/case/LeadFormPage';
import InterviewFormPage from './pages/case/InterviewFormPage';
import LocationFormPage from './pages/case/LocationFormPage';
import EntityViewPage from './pages/case/EntityViewPage';
import EntityGraphPage from './pages/EntityGraphPage';
import StatusChangePage from './pages/case/StatusChangePage';
import ShareCasePage from './pages/case/ShareCasePage';
import Invoices from './pages/Invoices';
import Settings from './pages/Settings';
import MainHubDashboard from './pages/MainHubDashboard';
import MainHubCompanies from './pages/MainHubCompanies';
import MainHubUsers from './pages/MainHubUsers';
import MainHubUserDetail from './pages/mainhub/MainHubUserDetail';
import ClientIntakes from './pages/ClientIntakes';
import SkipTraceTools from './pages/SkipTraceTools';
import AdminSkiptraceAudit from './pages/AdminSkiptraceAudit';
import AdminCertificateEditor from './pages/AdminCertificateEditor';
import ClientDocuments from './pages/ClientDocuments';
import ClientDocumentTemplates from './pages/ClientDocumentTemplates';
import FieldMap from './pages/FieldMap';
import Tutorials from './pages/Tutorials';
import PublicSignPage from './pages/PublicSignPage';
import AdminFileHistory from './pages/AdminFileHistory';
import InvestigatorTracking from './pages/InvestigatorTracking';
import CaseAddressMap from './pages/CaseAddressMap';
import CaseOperationsMap from './pages/CaseOperationsMap';
import PermissionsHub from './pages/PermissionsHub';
import CaseAcceptance from './pages/CaseAcceptance';
import CaseAcceptanceSign from './pages/CaseAcceptanceSign';
import ForgotPassword from './pages/ForgotPassword';
import Apply from './pages/Apply';
import ClientIntakeLanding from './pages/ClientIntakeLanding';
import NexusNewHire from './pages/NexusNewHire';
import RecruitmentCommandCenter from './pages/RecruitmentCommandCenter';
import TamsManagePage from './pages/recruitment/TamsManagePage';
import OnboardingDocuments from './pages/OnboardingDocuments';
import { NxShell } from './components/nx/NxShell';
import ApplicantLogin from './pages/ApplicantLogin';
import ApplicantPortal from './pages/ApplicantPortal';
import TrainingCenter from './pages/TrainingCenter';
import AIAssistant from './pages/AIAssistant';
import FieldOperations from './pages/FieldOperations';
import FieldOperationForm from './pages/FieldOperationForm';
import FieldOperationsTime from './pages/FieldOperationsTime';
import AdminFieldReports from './pages/AdminFieldReports';
import AdminTimesheets from './pages/AdminTimesheets';
import NexusInvestigatorActivity from './pages/NexusInvestigatorActivity';
import PublicTipPortal from './pages/PublicTipPortal';
import AdminTipsQueue from './pages/AdminTipsQueue';
import ClientRequestCenter from './pages/ClientRequestCenter';
import AdminEditApprovals from './pages/AdminEditApprovals';
import AdminMainHubPermissions from './pages/AdminMainHubPermissions';
import MainHubNotificationPermissions from './pages/MainHubNotificationPermissions';
import FieldCommandCenter from './pages/FieldCommandCenter';
import NexusTracControl from './pages/NexusTracControl';
import ClientsHub from './pages/ClientsHub';
import CaseLogsHub from './pages/CaseLogsHub';
import CertificatePreviewPage from './pages/training/CertificatePreviewPage';
import TrainingCourseDetail from './pages/training/CourseDetail';
import TrainingExamPlayer from './pages/training/ExamPlayer';
import TrainingCertificate from './pages/training/CertificateView';
import PendingCertificates from './pages/training/PendingCertificates';
import CourseBuilderList from './pages/training/CourseBuilderList';
import CourseBuilder from './pages/training/CourseBuilder';
import DirectoriesPage from './pages/DirectoriesPage';
import QuickNotesPage from './pages/QuickNotesPage';
import Messages from './pages/Messages';

// Layout
import { installHttpInterceptor } from './services/httpInterceptor';

installHttpInterceptor();
import MainHubLayout from './components/MainHubLayout';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], allowSetupBypass = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Newly-promoted users must finish forced username + password rotation
  // before they can access anything else. The /complete-setup route itself
  // opts out of this guard via `allowSetupBypass`.
  if (!allowSetupBypass && (user.require_username_change || user.require_password_change)) {
    return <Navigate to="/complete-setup" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Main Hub Protected Route
const MainHubRoute = ({ children }) => {
  const token = localStorage.getItem('nexus_main_hub_token');
  
  if (!token) {
    return <Navigate to="/main-hub/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route
            path="/complete-setup"
            element={
              <ProtectedRoute allowSetupBypass>
                <CompleteSetup />
              </ProtectedRoute>
            }
          />
          <Route path="/main-hub/login" element={<MainHubLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/client-intake" element={<ClientIntakeLanding />} />
          <Route path="/applicant/login" element={<ApplicantLogin />} />
          <Route path="/applicant-portal" element={<ApplicantPortal />} />
          <Route path="/applicant/portal" element={<ApplicantPortal />} />

          {/* Iter123 — Public Tip Portal (no auth) */}
          <Route path="/tip" element={<PublicTipPortal />} />
          <Route path="/tips" element={<PublicTipPortal />} />
          <Route path="/submit-tip" element={<PublicTipPortal />} />

          {/* Legacy client-portal routes — the Client Portal has been retired
              (Iter120L). Every route redirects to the unified /login where
              clients now sign in with their existing credentials. */}
          <Route path="/client/login" element={<Navigate to="/login" replace />} />
          <Route path="/client/forgot-password" element={<Navigate to="/forgot-password" replace />} />
          <Route path="/client-portal" element={<Navigate to="/login" replace />} />
          <Route path="/client-portal/landing" element={<Navigate to="/login" replace />} />
          <Route path="/client-portal/new-intake" element={<Navigate to="/login" replace />} />
          <Route path="/client-portal/case/:caseId" element={<Navigate to="/login" replace />} />
          <Route path="/client-portal/change-password" element={<Navigate to="/complete-setup" replace />} />

          {/* Iter131f — Public magic-link signing (no auth). */}
          <Route path="/sign/:documentId" element={<PublicSignPage />} />
          
          {/* Redirect root to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* Main Hub Routes */}
          <Route path="/main-hub" element={
            <MainHubRoute>
              <MainHubLayout />
            </MainHubRoute>
          }>
            <Route index element={<MainHubDashboard />} />
            <Route path="dashboard" element={<MainHubDashboard />} />
            <Route path="companies" element={<MainHubCompanies />} />
            <Route path="users" element={<MainHubUsers />} />
            <Route path="permissions" element={<PermissionsHub />} />
          </Route>
          
          {/* Protected App Routes — NEW Tactical MDT Shell */}
          <Route path="/" element={
            <ProtectedRoute>
              <NxShell />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<ModuleGuard moduleId="dashboard"><Dashboard /></ModuleGuard>} />
            <Route path="cases" element={<ModuleGuard moduleId="cases"><Cases /></ModuleGuard>} />
            <Route path="cases/new" element={<ModuleGuard moduleId="cases"><CaseCreatePage /></ModuleGuard>} />
            <Route path="cases/:caseId" element={<ModuleGuard moduleId="cases"><CaseDetail /></ModuleGuard>} />
            <Route path="cases/:caseId/ops-plan/:planId" element={<ModuleGuard moduleId="cases"><OpsPlanSharedPage /></ModuleGuard>} />
            <Route path="cases/:caseId/evidence/new" element={<ModuleGuard moduleId="evidence"><EvidenceUploadPage /></ModuleGuard>} />
            <Route path="cases/:caseId/evidence/:evidenceId/preview" element={<ModuleGuard moduleId="evidence"><EvidencePreviewPage /></ModuleGuard>} />
            <Route path="evidence/:evidenceId/preview" element={<ModuleGuard moduleId="evidence"><EvidencePreviewPage /></ModuleGuard>} />
            <Route path="cases/:caseId/subjects/new" element={<ModuleGuard moduleId="cases"><SubjectFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/subjects/:subjectId/edit" element={<ModuleGuard moduleId="cases"><SubjectFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/vehicles/new" element={<ModuleGuard moduleId="cases"><VehicleFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/vehicles/:vehicleId/edit" element={<ModuleGuard moduleId="cases"><VehicleFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/logs/new" element={<ModuleGuard moduleId="case_logs"><LogFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/notes/new" element={<ModuleGuard moduleId="case_notes"><NoteFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/notes/:noteId/edit" element={<ModuleGuard moduleId="case_notes"><NoteFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/leads/new" element={<ModuleGuard moduleId="cases"><LeadFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/leads/:leadId/edit" element={<ModuleGuard moduleId="cases"><LeadFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/interviews/new" element={<ModuleGuard moduleId="cases"><InterviewFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/interviews/:interviewId/edit" element={<ModuleGuard moduleId="cases"><InterviewFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/locations/new" element={<ModuleGuard moduleId="cases"><LocationFormPage /></ModuleGuard>} />
            <Route path="cases/:caseId/locations/:locationId/edit" element={<ModuleGuard moduleId="cases"><LocationFormPage /></ModuleGuard>} />
            {/* Iter121G — generic View pages for every case entity */}
            <Route path="cases/:caseId/:entity/:id/view" element={<ModuleGuard moduleId="cases"><EntityViewPage /></ModuleGuard>} />
            {/* Iter121I — Intelligence / Entity Graph */}
            <Route path="entity-graph" element={<EntityGraphPage />} />
            <Route path="cases/:caseId/status" element={<ModuleGuard moduleId="cases"><StatusChangePage /></ModuleGuard>} />
            <Route path="cases/:caseId/share" element={<ModuleGuard moduleId="cases"><ShareCasePage /></ModuleGuard>} />
            <Route path="case-logs" element={<ModuleGuard moduleId="case_logs"><CaseLogs /></ModuleGuard>} />
            <Route path="case-notes" element={<ModuleGuard moduleId="case_notes"><CaseNotes /></ModuleGuard>} />
            <Route path="evidence" element={<ModuleGuard moduleId="evidence"><Evidence /></ModuleGuard>} />
            <Route path="reports" element={<ModuleGuard moduleId="reports"><Reports /></ModuleGuard>} />
            <Route path="reports/create" element={<ModuleGuard moduleId="reports"><CreateReport /></ModuleGuard>} />
            <Route path="reports/create/:caseId" element={<ModuleGuard moduleId="reports"><CreateReport /></ModuleGuard>} />
            <Route path="clients" element={<ModuleGuard moduleId="clients"><Clients /></ModuleGuard>} />
            <Route path="clients/new" element={<ModuleGuard moduleId="clients"><ClientFormPage /></ModuleGuard>} />
            <Route path="clients/:clientId/edit" element={<ModuleGuard moduleId="clients"><ClientFormPage /></ModuleGuard>} />
            <Route path="clients/:clientId/view" element={<ModuleGuard moduleId="clients"><ClientViewPage /></ModuleGuard>} />
            <Route path="invoices" element={<ModuleGuard moduleId="invoices"><Invoices /></ModuleGuard>} />
            <Route path="team" element={<ModuleGuard moduleId="team"><TeamManagement /></ModuleGuard>} />
            <Route path="team/new" element={<ModuleGuard moduleId="team"><TeamMemberFormPage /></ModuleGuard>} />
            <Route path="team/:userId/edit" element={<ModuleGuard moduleId="team"><TeamMemberFormPage /></ModuleGuard>} />
            <Route path="analytics" element={<ModuleGuard moduleId="analytics"><Analytics /></ModuleGuard>} />
            <Route path="settings" element={<ModuleGuard moduleId="settings"><Settings /></ModuleGuard>} />
            <Route path="skip-trace" element={<ModuleGuard moduleId="skip_trace"><SkipTraceTools /></ModuleGuard>} />
            <Route path="admin/skip-trace-audit" element={<AdminSkiptraceAudit />} />
            <Route path="admin/certificate-editor" element={<AdminCertificateEditor />} />
            <Route path="client-documents" element={<ClientDocuments />} />
            <Route path="client-documents/templates" element={<ClientDocumentTemplates />} />
            <Route path="field-map" element={<FieldMap />} />
            <Route path="tutorials" element={<Tutorials />} />
            <Route path="client-portal/documents" element={<ClientDocuments />} />
            <Route path="admin/file-history" element={<AdminFileHistory />} />
            <Route path="investigator-tracking" element={<ModuleGuard moduleId="gps_tracking"><InvestigatorTracking /></ModuleGuard>} />
            <Route path="case-address-map" element={<ModuleGuard moduleId="address_intel_map"><CaseAddressMap /></ModuleGuard>} />
            <Route path="case-operations" element={<ModuleGuard moduleId="operations_map"><CaseOperationsMap /></ModuleGuard>} />
            <Route path="intakes" element={<ModuleGuard moduleId="client_intakes"><ClientIntakes /></ModuleGuard>} />
            <Route path="case-acceptance" element={<ModuleGuard moduleId="case_acceptance"><CaseAcceptance /></ModuleGuard>} />
            <Route path="case-acceptance/:formId" element={<ModuleGuard moduleId="case_acceptance"><CaseAcceptanceSign /></ModuleGuard>} />
            <Route path="nexus-new-hire" element={<ModuleGuard moduleId="recruitment"><RecruitmentCommandCenter /></ModuleGuard>} />
            <Route path="nexus-new-hire/tams-manage" element={<ModuleGuard moduleId="recruitment"><TamsManagePage /></ModuleGuard>} />
            <Route path="recruitment/applicants/:applicantId" element={<ModuleGuard moduleId="recruitment"><ApplicantReviewPage /></ModuleGuard>} />
            <Route path="onboarding-documents" element={<ModuleGuard moduleId="recruitment"><OnboardingDocuments /></ModuleGuard>} />
            <Route path="nexus-new-hire-legacy" element={<ModuleGuard moduleId="recruitment"><NexusNewHire /></ModuleGuard>} />
            <Route path="training-center" element={<ModuleGuard moduleId="training_center"><TrainingCenter /></ModuleGuard>} />
            <Route path="training-center/course/:courseId" element={<ModuleGuard moduleId="training_center"><TrainingCourseDetail /></ModuleGuard>} />
            <Route path="training-center/course/:courseId/exam" element={<ModuleGuard moduleId="training_center"><TrainingExamPlayer /></ModuleGuard>} />
            <Route path="training-center/certificate/:verifyUuid" element={<ModuleGuard moduleId="training_center"><TrainingCertificate /></ModuleGuard>} />
            <Route path="admin/certifications" element={<ModuleGuard moduleId="training_center"><PendingCertificates /></ModuleGuard>} />
            <Route path="admin/course-builder" element={<ModuleGuard moduleId="training_center"><CourseBuilderList /></ModuleGuard>} />
            <Route path="admin/course-builder/:courseId" element={<ModuleGuard moduleId="training_center"><CourseBuilder /></ModuleGuard>} />
            {/* Dashboard 3.0 — Command Center modules */}
            <Route path="directories" element={<DirectoriesPage />} />
            <Route path="quick-notes" element={<QuickNotesPage />} />
            <Route path="messages" element={<ModuleGuard moduleId="messaging"><Messages /></ModuleGuard>} />
            {/* Spec-compatibility redirects (ITER86-009) */}
            <Route path="ai-assistant" element={<ModuleGuard moduleId="ai_investigation"><AIAssistant /></ModuleGuard>} />
            <Route path="ai-investigation" element={<ModuleGuard moduleId="ai_investigation"><AIAssistant /></ModuleGuard>} />
            <Route path="field-operations" element={<ModuleGuard moduleId="field_operations"><FieldOperations /></ModuleGuard>} />
            <Route path="field-operations/time" element={<FieldOperationsTime />} />
            <Route path="my-time-clock" element={<FieldOperationsTime />} />
            <Route path="my-time-clock/operations" element={<ModuleGuard moduleId="field_operations"><FieldOperations /></ModuleGuard>} />
            <Route path="my-time-clock/operations/new" element={<ModuleGuard moduleId="field_operations"><FieldOperationForm /></ModuleGuard>} />
            <Route path="field-operations/admin-reports" element={<AdminFieldReports />} />
            <Route path="admin/timesheets" element={<AdminTimesheets />} />
            <Route path="team/activity" element={<ProtectedRoute allowedRoles={['main_hub','super_admin','department_admin']}><NexusInvestigatorActivity /></ProtectedRoute>} />
            <Route path="field-operations/new" element={<ModuleGuard moduleId="field_operations"><FieldOperationForm /></ModuleGuard>} />
            <Route path="field-operations/:opId/edit" element={<ModuleGuard moduleId="field_operations"><FieldOperationForm /></ModuleGuard>} />
            <Route path="field-operations/:opId" element={<ModuleGuard moduleId="field_operations"><FieldOperations /></ModuleGuard>} />

            {/* Iter123 — Tips, Requests, Approvals, Permissions */}
            <Route path="admin/tips" element={<AdminTipsQueue />} />
            <Route path="admin/edit-approvals" element={<AdminEditApprovals />} />
            <Route path="admin/permissions" element={<AdminMainHubPermissions />} />
            <Route path="admin/notification-permissions" element={<MainHubNotificationPermissions />} />
            <Route path="field-command" element={<FieldCommandCenter />} />
            <Route path="admin/control" element={<NexusTracControl />} />
            <Route path="clients-hub" element={<ClientsHub />} />
            <Route path="case-logs-hub" element={<CaseLogsHub />} />
            <Route path="client-requests" element={<ClientRequestCenter />} />
            <Route path="admin/certifications/preview/:certId" element={<CertificatePreviewPage />} />
            <Route path="notifications" element={<Navigate to="/dashboard" replace />} />
            <Route path="recruitment-command-center" element={<Navigate to="/nexus-new-hire" replace />} />
            <Route path="ops-map" element={<Navigate to="/case-operations" replace />} />
            <Route path="team-management" element={<Navigate to="/team" replace />} />
            <Route path="tams" element={<Navigate to="/nexus-new-hire/tams-manage" replace />} />
            <Route path="client-intakes" element={<Navigate to="/intakes" replace />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
      <Toaster />
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
